import React, { useState, useEffect } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import { ethers } from 'ethers';
import { useTranslation } from 'react-i18next';
import { notify } from '@/utils/notify';
import '@/styles/hacker-aesthetic.css';

interface AdminPanelProps {
  onClose: () => void;
}

interface GameSettings {
  nodePrice: string;
  tier2NodePrice: string;
  tier3NodePrice: string;
  tier4NodePrice: string;
  tier5NodePrice: string;
  chestPrice: string;
  chestTierPrices: string[];
  rewardRate: string;
  upgradeBasePrice: string;
  referralReward: string;
  referralBonus: string;
  reactivationFee: string;
  protocolFeeBps: string;
  treasury: string;
  gamePaused: boolean;
  achievementRewards: string[];
  nodeMaxStats: { [tier: number]: { hashPower: string; bandwidth: string; uptime: string } };
  upgradeBoosts: string[];
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ onClose }) => {
  const { contracts, signer, isConnected } = useNetwork();
  const { t } = useTranslation();
  const [settings, setSettings] = useState<GameSettings>({
    nodePrice: '100',
    tier2NodePrice: '25',
    tier3NodePrice: '50',
    tier4NodePrice: '100',
    tier5NodePrice: '250',
    chestPrice: '50',
    chestTierPrices: ['50', '50', '50', '50', '50'],
    rewardRate: '0.001',
    upgradeBasePrice: '10',
    referralReward: '5',
    referralBonus: '5',
    reactivationFee: '2',
    protocolFeeBps: '1500',
    treasury: '',
    gamePaused: false,
    achievementRewards: ['1', '3', '5', '5', '15', '5', '20', '5', '50', '100', '200'],
    nodeMaxStats: {
      1: { hashPower: '100', bandwidth: '100', uptime: '100' },
      2: { hashPower: '250', bandwidth: '250', uptime: '250' },
      3: { hashPower: '500', bandwidth: '500', uptime: '500' },
      4: { hashPower: '1000', bandwidth: '1000', uptime: '1000' },
      5: { hashPower: '2500', bandwidth: '2500', uptime: '2500' }
    },
    upgradeBoosts: ['10', '20', '30', '40', '50']
  });
  const [loading, setLoading] = useState(false);
  const [contractBalances, setContractBalances] = useState({
    gameLogic: '0',
    achievements: '0',
    rewardsPool: '0'
  });

  useEffect(() => {
    if (isConnected && contracts) {
      loadSettings();
      loadBalances();
    }
  }, [isConnected, contracts]);

  const loadSettings = async () => {
    if (!contracts.gameLogic) return;

    try {
      setLoading(true);
      
      const nodePrice = await contracts.gameLogic.nodePurchasePrice();
      const tier2NodePrice = await contracts.gameLogic.tier2NodePrice();
      const tier3NodePrice = await contracts.gameLogic.tier3NodePrice();
      const tier4NodePrice = await contracts.gameLogic.tier4NodePrice();
      const tier5NodePrice = await contracts.gameLogic.tier5NodePrice();
      const chestPrice = await contracts.gameLogic.chestPurchasePrice();
      const rewardRate = await contracts.gameLogic.rewardRatePerHour();
      const upgradeBasePrice = await contracts.gameLogic.upgradeBasePrice();
      const referralReward = await contracts.gameLogic.referralReward();
      const referralBonus = await contracts.gameLogic.referralReward(); // Use referralReward instead of referrerBonusPercent
      const paused = await contracts.gameLogic.paused();
      let reactivationFee = '2';
      let protocolFeeBps = '1500';
      let treasury = '';
      try {
        reactivationFee = ethers.formatEther(await contracts.gameLogic.reactivationFee());
        protocolFeeBps = (await contracts.gameLogic.protocolFeeBps()).toString();
        treasury = await contracts.gameLogic.treasury();
      } catch {
        // Older deployments may lack sink knobs
      }
      
      // Load individual chest tier prices
      const chestTierPrices: string[] = [];
      for (let i = 1; i <= 5; i++) {
        const price = await contracts.gameLogic.chestTierPrices(i);
        chestTierPrices.push(ethers.formatEther(price));
      }
      
      // Load achievement rewards
      const achievementRewards: string[] = [];
      for (let i = 0; i < 11; i++) {
        try {
          const achievement = await contracts.achievements.getAchievement(i);
          achievementRewards.push(ethers.formatEther(achievement.rewardAmount));
        } catch (error) {
          achievementRewards.push('0');
        }
      }

      // Load node max stats from VirtualNodeNFT
      const nodeMaxStats: { [tier: number]: { hashPower: string; bandwidth: string; uptime: string } } = {};
      if (contracts.nodeNFT) {
        for (let tier = 1; tier <= 5; tier++) {
          try {
            const hashMax = await contracts.nodeNFT.maxHashPower(tier);
            const bandMax = await contracts.nodeNFT.maxBandwidth(tier);
            const uptimeMax = await contracts.nodeNFT.maxUptime(tier);
            
            nodeMaxStats[tier] = {
              hashPower: hashMax.toString(),
              bandwidth: bandMax.toString(),
              uptime: uptimeMax.toString()
            };
          } catch (error) {
            nodeMaxStats[tier] = { hashPower: '100', bandwidth: '100', uptime: '100' };
          }
        }
      }

      // Load upgrade boosts
      const upgradeBoosts: string[] = [];
      for (let rarity = 1; rarity <= 5; rarity++) {
        try {
          const boost = await contracts.gameLogic.getUpgradeBoost(rarity);
          upgradeBoosts.push(boost.toString());
        } catch (error) {
          upgradeBoosts.push('10');
        }
      }
      
      setSettings(prevSettings => ({
        ...prevSettings,
        nodePrice: ethers.formatEther(nodePrice),
        tier2NodePrice: ethers.formatEther(tier2NodePrice),
        tier3NodePrice: ethers.formatEther(tier3NodePrice),
        tier4NodePrice: ethers.formatEther(tier4NodePrice),
        tier5NodePrice: ethers.formatEther(tier5NodePrice),
        chestPrice: ethers.formatEther(chestPrice),
        chestTierPrices: chestTierPrices,
        rewardRate: ethers.formatEther(rewardRate),
        upgradeBasePrice: ethers.formatEther(upgradeBasePrice),
        referralReward: ethers.formatEther(referralReward),
        referralBonus: ethers.formatEther(referralBonus),
        reactivationFee,
        protocolFeeBps,
        treasury,
        gamePaused: paused,
        achievementRewards: achievementRewards,
        nodeMaxStats: nodeMaxStats,
        upgradeBoosts: upgradeBoosts
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBalances = async () => {
    if (!contracts.gameLogic || !contracts.achievements || !contracts.rewardsPool) return;

    try {
      // Get ETCPOWv2 token balances of contracts
      const gameLogicBalance = await contracts.etcpowv2Token.balanceOf(await contracts.gameLogic.getAddress());
      const achievementsBalance = await contracts.etcpowv2Token.balanceOf(await contracts.achievements.getAddress());
      const rewardsPoolBalance = await contracts.etcpowv2Token.balanceOf(await contracts.rewardsPool.getAddress());
      
      setContractBalances({
        gameLogic: ethers.formatEther(gameLogicBalance),
        achievements: ethers.formatEther(achievementsBalance),
        rewardsPool: ethers.formatEther(rewardsPoolBalance)
      });
    } catch (error) {
      console.error('Failed to load balances:', error);
    }
  };

  const updateAllAchievementRewards = async () => {
    if (!contracts.achievements || !signer) return;

    try {
      setLoading(true);
      
      const achievementIds = Array.from({length: 11}, (_, i) => i);
      const rewardAmounts = settings.achievementRewards.map(reward => ethers.parseEther(reward));
      
      const tx = await contracts.achievements.updateMultipleAchievementRewards(
        achievementIds,
        rewardAmounts
      );
      
      await tx.wait();
      notify.success('All achievement rewards updated');
    } catch (error: any) {
      console.error('Failed to update achievement rewards:', error);
      notify.error(error, 'Failed to update achievement rewards');
    } finally {
      setLoading(false);
    }
  };

  const updateAllChestTierPrices = async () => {
    if (!contracts.gameLogic || !signer) return;

    try {
      setLoading(true);
      
      const tierPrices = settings.chestTierPrices.map(price => ethers.parseEther(price));
      
      const tx = await contracts.gameLogic.setAllChestTierPrices(
        tierPrices[0],
        tierPrices[1],
        tierPrices[2],
        tierPrices[3],
        tierPrices[4]
      );
      
      await tx.wait();
      
      // Reload settings
      await loadSettings();
      notify.success('All chest tier prices updated');
    } catch (error: any) {
      console.error('Failed to update chest tier prices:', error);
      notify.error(error, 'Failed to update chest tier prices');
    } finally {
      setLoading(false);
    }
  };

  const updateNodeMaxStats = async (tier: number) => {
    if (!contracts.nodeNFT || !signer) return;

    try {
      setLoading(true);
      
      const stats = settings.nodeMaxStats[tier];
      
      const tx = await contracts.nodeNFT.setMaxStatsForTier(
        tier,
        parseInt(stats.hashPower),
        parseInt(stats.bandwidth),
        parseInt(stats.uptime)
      );
      
      await tx.wait();
      
      // Reload settings
      await loadSettings();
      notify.success(`Tier ${tier} max stats updated`);
    } catch (error: any) {
      console.error('Failed to update node max stats:', error);
      notify.error(error, 'Failed to update tier ${tier} max stats');
    } finally {
      setLoading(false);
    }
  };

  const updateAllNodeMaxStats = async () => {
    if (!contracts.nodeNFT || !signer) return;

    try {
      setLoading(true);
      
      const hashPowers: number[] = [];
      const bandwidths: number[] = [];
      const uptimes: number[] = [];
      
      for (let tier = 1; tier <= 5; tier++) {
        const stats = settings.nodeMaxStats[tier];
        hashPowers.push(parseInt(stats.hashPower));
        bandwidths.push(parseInt(stats.bandwidth));
        uptimes.push(parseInt(stats.uptime));
      }
      
      const tx = await contracts.nodeNFT.setAllMaxStats(hashPowers, bandwidths, uptimes);
      
      await tx.wait();
      
      // Reload settings
      await loadSettings();
      notify.success('All node max stats updated');
    } catch (error: any) {
      console.error('Failed to update all node max stats:', error);
      notify.error(error, 'Failed to update all node max stats');
    } finally {
      setLoading(false);
    }
  };

  const updateUpgradeBoost = async (rarity: number) => {
    if (!contracts.gameLogic || !signer) return;

    try {
      setLoading(true);
      
      const tx = await contracts.gameLogic.setUpgradeBoost(
        rarity,
        parseInt(settings.upgradeBoosts[rarity - 1])
      );
      
      await tx.wait();
      
      // Reload settings
      await loadSettings();
      notify.success(`Rarity ${rarity} boost updated`);
    } catch (error: any) {
      console.error('Failed to update upgrade boost:', error);
      notify.error(error, 'Failed to update rarity ${rarity} boost');
    } finally {
      setLoading(false);
    }
  };

  const updateAllUpgradeBoosts = async () => {
    if (!contracts.gameLogic || !signer) return;

    try {
      setLoading(true);
      
      const boosts = settings.upgradeBoosts.map(boost => parseInt(boost));
      
      const tx = await contracts.gameLogic.setAllUpgradeBoosts(boosts);
      
      await tx.wait();
      
      // Reload settings
      await loadSettings();
      notify.success('All upgrade boosts updated');
    } catch (error: any) {
      console.error('Failed to update all upgrade boosts:', error);
      notify.error(error, 'Failed to update all upgrade boosts');
    } finally {
      setLoading(false);
    }
  };

  const updateAchievementReward = async (achievementId: number, newReward: string) => {
    if (!contracts.achievements || !signer) return;

    try {
      setLoading(true);
      
      const tx = await contracts.achievements.updateAchievementReward(
        achievementId,
        ethers.parseEther(newReward)
      );
      
      await tx.wait();
      
      // Reload settings
      await loadSettings();
      notify.success(`Achievement ${achievementId + 1} reward updated`);
    } catch (error: any) {
      console.error('Failed to update achievement reward:', error);
      notify.error(error, 'Failed to update achievement ${achievementId + 1} reward');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (setting: string, value: string | number | boolean) => {
    if (!contracts.gameLogic || !signer) return;

    try {
      setLoading(true);
      let tx;

      switch (setting) {
        case 'nodePrice':
          tx = await contracts.gameLogic.setNodePurchasePrice(ethers.parseEther(value as string));
          break;
        case 'chestPrice':
          tx = await contracts.gameLogic.setChestPurchasePrice(ethers.parseEther(value as string));
          break;
        case 'tier2NodePrice':
          tx = await contracts.gameLogic.setTier2NodePrice(ethers.parseEther(value as string));
          break;
        case 'tier3NodePrice':
          tx = await contracts.gameLogic.setTier3NodePrice(ethers.parseEther(value as string));
          break;
        case 'tier4NodePrice':
          tx = await contracts.gameLogic.setTier4NodePrice(ethers.parseEther(value as string));
          break;
        case 'tier5NodePrice':
          tx = await contracts.gameLogic.setTier5NodePrice(ethers.parseEther(value as string));
          break;
        case 'rewardRate':
          tx = await contracts.gameLogic.setRewardRatePerHour(ethers.parseEther(value as string));
          break;
        case 'upgradeBasePrice':
          tx = await contracts.gameLogic.setUpgradeBasePrice(ethers.parseEther(value as string));
          break;
        case 'referralReward':
          tx = await contracts.gameLogic.setReferralReward(ethers.parseEther(value as string));
          break;
        case 'referralBonus':
          tx = await contracts.gameLogic.setReferralBonus(ethers.parseEther(value as string));
          break;
        case 'reactivationFee':
          tx = await contracts.gameLogic.setReactivationFee(ethers.parseEther(value as string));
          break;
        case 'protocolFeeBps':
          tx = await contracts.gameLogic.setProtocolFeeBps(BigInt(value as string));
          break;
        case 'treasury':
          tx = await contracts.gameLogic.setTreasury(value as string);
          break;
        case 'gamePaused':
          tx = await contracts.gameLogic.setPaused(value as boolean);
          break;
        default:
          return;
      }

      await tx.wait();
      
      // Reload settings
      await loadSettings();
      notify.success(`${setting} updated`);
    } catch (error: any) {
      console.error('Failed to update setting:', error);
      notify.error(error, 'Failed to update ${setting}');
    } finally {
      setLoading(false);
    }
  };

  const fundContract = async (contract: 'gameLogic' | 'achievements', amount: string) => {
    if (!contracts.etcpowv2Token || !signer) return;

    try {
      setLoading(true);
      const contractAddress = contract === 'gameLogic' 
        ? await contracts.gameLogic.getAddress()
        : await contracts.achievements.getAddress();
      
      const amountWei = ethers.parseEther(amount);
      
      // Approve spending
      await contracts.etcpowv2Token.approve(contractAddress, amountWei);
      
      // Transfer tokens
      if (contract === 'gameLogic') {
        await contracts.etcpowv2Token.transfer(contractAddress, amountWei);
      } else {
        await contracts.etcpowv2Token.transfer(contractAddress, amountWei);
      }
      
      // Reload balances
      await loadBalances();
      notify.success(`Funded ${contract} with ${amount} ETCPOWv2`);
    } catch (error: any) {
      console.error('Failed to fund contract:', error);
      notify.error(error, 'Failed to fund contract');
    } finally {
      setLoading(false);
    }
  };

  const fundRewardsPool = async (amount: string) => {
    if (!contracts.etcpowv2Token || !contracts.rewardsPool || !signer) return;

    try {
      setLoading(true);
      const poolAddress = await contracts.rewardsPool.getAddress();
      const amountWei = ethers.parseEther(amount);

      // deposit() pulls via transferFrom, so approve then deposit (keeps totalDeposited accurate)
      const approveTx = await contracts.etcpowv2Token.approve(poolAddress, amountWei);
      await approveTx.wait();
      const depositTx = await contracts.rewardsPool.deposit(amountWei);
      await depositTx.wait();

      await loadBalances();
      notify.success(`Deposited ${amount} ETCPOWv2 into the Rewards Pool`);
    } catch (error: any) {
      console.error('Failed to fund rewards pool:', error);
      notify.error(error, 'Failed to fund rewards pool');
    } finally {
      setLoading(false);
    }
  };

  const withdrawFromContract = async (contract: 'gameLogic' | 'achievements' | 'rewardsPool', amount: string) => {
    if (!signer) return;

    try {
      setLoading(true);
      let tx;
      const amountWei = amount === '0' ? undefined : ethers.parseEther(amount);

      switch (contract) {
        case 'gameLogic':
          if (amount === '0') {
            tx = await contracts.gameLogic.emergencyWithdrawTokens();
          } else {
            tx = await contracts.gameLogic.withdrawTokens(amountWei);
          }
          break;
        case 'achievements':
          if (amount === '0') {
            // Get balance first for full withdrawal
            const balance = await contracts.etcpowv2Token.balanceOf(await contracts.achievements.getAddress());
            tx = await contracts.achievements.withdrawExcessTokens(balance);
          } else {
            tx = await contracts.achievements.withdrawExcessTokens(amountWei);
          }
          break;
        case 'rewardsPool':
          tx = await contracts.rewardsPool.emergencyWithdraw();
          break;
        default:
          return;
      }

      await tx.wait();
      
      // Reload balances
      await loadBalances();
      notify.success(`Withdrew tokens from ${contract}`);
    } catch (error: any) {
      console.error('Failed to withdraw from contract:', error);
      notify.error(error, 'Failed to withdraw from ${contract}');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <div className="panel-title">{t('admin.title')}</div>
        <div className="panel-stats">
          <span className="stat-item">
            {t('admin.owner_mode')}
          </span>
        </div>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      <div className="admin-content">
        {/* Price Controls */}
        <div className="admin-section">
          <h3 className="section-title">{t('admin.price_controls')}</h3>
          <div className="control-grid">
            <div className="control-item">
              <label>{t('admin.node_price')}</label>
              <input
                type="number"
                value={settings.nodePrice}
                onChange={(e) => setSettings({...settings, nodePrice: e.target.value})}
                className="admin-input"
              />
              <button 
                onClick={() => updateSetting('nodePrice', settings.nodePrice)}
                className="admin-button"
                disabled={loading}
              >
                {t('admin.update')}
              </button>
            </div>

            <div className="control-item">
              <label>Tier 2-5 Node Prices</label>
              <div className="chest-tier-prices">
                {[
                  { key: 'tier2NodePrice', label: 'Tier 2' },
                  { key: 'tier3NodePrice', label: 'Tier 3' },
                  { key: 'tier4NodePrice', label: 'Tier 4' },
                  { key: 'tier5NodePrice', label: 'Tier 5' },
                ].map(({ key, label }) => (
                  <div key={key} className="tier-price-input">
                    <span>{label}:</span>
                    <input
                      type="number"
                      value={(settings as any)[key]}
                      onChange={(e) => setSettings({ ...settings, [key]: e.target.value } as GameSettings)}
                      className="admin-input small"
                    />
                    <button
                      onClick={() => updateSetting(key, (settings as any)[key])}
                      className="admin-button small"
                      disabled={loading}
                    >
                      Set
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="control-item">
              <label>{t('admin.chest_prices')}</label>
              <div className="chest-tier-prices">
                {[t('admin.chest_tier_basic'), t('admin.chest_tier_uncommon'), t('admin.chest_tier_rare'), t('admin.chest_tier_epic'), t('admin.chest_tier_legendary')].map((tier, index) => (
                  <div key={tier} className="tier-price-input">
                    <span>{tier}:</span>
                    <input
                      type="number"
                      value={settings.chestTierPrices[index]}
                      onChange={(e) => {
                        const newPrices = [...settings.chestTierPrices];
                        newPrices[index] = e.target.value;
                        setSettings({...settings, chestTierPrices: newPrices});
                      }}
                      className="admin-input small"
                    />
                  </div>
                ))}
              </div>
              <div className="tier-price-buttons">
                <button 
                  onClick={() => updateAllChestTierPrices()}
                  className="admin-button"
                  disabled={loading}
                >
                  Update All
                </button>
                <button 
                  onClick={() => updateSetting('chestPrice', settings.chestPrice)}
                  className="admin-button secondary"
                  disabled={loading}
                >
                  Set Base Price
                </button>
              </div>
            </div>
            
            <div className="control-item">
              <label>{t('admin.achievement_rewards')}</label>
              <div className="achievement-rewards-grid">
                {[
                  t('achievement.node_pioneer'),
                  t('achievement.node_collector'),
                  t('achievement.node_master'),
                  t('achievement.upgrade_beginner'),
                  t('achievement.upgrade_expert'),
                  t('achievement.lucky_opener'),
                  t('achievement.chest_hunter'),
                  t('achievement.first_claim'),
                  t('achievement.reward_collector'),
                  t('achievement.early_adopter'),
                  t('achievement.whale')
                ].map((achievementName, index) => (
                  <div key={achievementName} className="achievement-reward-input">
                    <span>{achievementName}:</span>
                    <input
                      type="number"
                      value={settings.achievementRewards[index]}
                      onChange={(e) => {
                        const newRewards = [...settings.achievementRewards];
                        newRewards[index] = e.target.value;
                        setSettings({...settings, achievementRewards: newRewards});
                      }}
                      className="admin-input small"
                    />
                    <button 
                      onClick={() => updateAchievementReward(index, settings.achievementRewards[index])}
                      className="admin-button small"
                      disabled={loading}
                    >
                      Update
                    </button>
                  </div>
                ))}
              </div>
              <div className="achievement-reward-buttons">
                <button 
                  onClick={() => updateAllAchievementRewards()}
                  className="admin-button"
                  disabled={loading}
                >
                  Update All Rewards
                </button>
              </div>
            </div>
            
            <div className="control-item">
              <label>{t('admin.reward_rate')}</label>
              <input
                type="number"
                step="0.0001"
                value={settings.rewardRate}
                onChange={(e) => setSettings({...settings, rewardRate: e.target.value})}
                className="admin-input"
              />
              <button 
                onClick={() => updateSetting('rewardRate', settings.rewardRate)}
                className="admin-button"
                disabled={loading}
              >
                {t('admin.update')}
              </button>
            </div>
            
            <div className="control-item">
              <label>{t('admin.upgrade_price')}</label>
              <input
                type="number"
                value={settings.upgradeBasePrice}
                onChange={(e) => setSettings({...settings, upgradeBasePrice: e.target.value})}
                className="admin-input"
              />
              <button 
                onClick={() => updateSetting('upgradeBasePrice', settings.upgradeBasePrice)}
                className="admin-button"
                disabled={loading}
              >
                {t('admin.update')}
              </button>
            </div>
          </div>
        </div>

        {/* Referral Controls */}
        <div className="admin-section">
          <h3 className="section-title">{t('admin.referral_controls')}</h3>
          <div className="control-grid">
            <div className="control-item">
              <label>{t('admin.referral_reward')}</label>
              <input
                type="number"
                value={settings.referralReward}
                onChange={(e) => setSettings({...settings, referralReward: e.target.value})}
                className="admin-input"
              />
              <button 
                onClick={() => updateSetting('referralReward', settings.referralReward)}
                className="admin-button"
                disabled={loading}
              >
                {t('admin.update')}
              </button>
            </div>
            
            <div className="control-item">
              <label>{t('admin.referral_bonus')}</label>
              <input
                type="number"
                value={settings.referralBonus}
                onChange={(e) => setSettings({...settings, referralBonus: e.target.value})}
                className="admin-input"
              />
              <button 
                onClick={() => updateSetting('referralBonus', settings.referralBonus)}
                className="admin-button"
                disabled={loading}
              >
                {t('admin.set')}
              </button>
            </div>

            <div className="control-item">
              <label>Reactivation Fee (ETCPOWv2)</label>
              <input
                type="number"
                value={settings.reactivationFee}
                onChange={(e) => setSettings({...settings, reactivationFee: e.target.value})}
                className="admin-input"
              />
              <button
                onClick={() => updateSetting('reactivationFee', settings.reactivationFee)}
                className="admin-button"
                disabled={loading}
              >
                {t('admin.update')}
              </button>
            </div>

            <div className="control-item">
              <label>Protocol Fee (bps)</label>
              <input
                type="number"
                value={settings.protocolFeeBps}
                onChange={(e) => setSettings({...settings, protocolFeeBps: e.target.value})}
                className="admin-input"
                placeholder="1500 = 15%"
              />
              <button
                onClick={() => updateSetting('protocolFeeBps', settings.protocolFeeBps)}
                className="admin-button"
                disabled={loading}
              >
                {t('admin.update')}
              </button>
            </div>

            <div className="control-item">
              <label>Treasury Address</label>
              <input
                type="text"
                value={settings.treasury}
                onChange={(e) => setSettings({...settings, treasury: e.target.value})}
                className="admin-input"
                placeholder="0x..."
              />
              <button
                onClick={() => updateSetting('treasury', settings.treasury)}
                className="admin-button"
                disabled={loading}
              >
                {t('admin.set')}
              </button>
            </div>

            <div className="control-item">
              <label>Game Status</label>
              <div className="game-status">
                <span className={`status-indicator ${settings.gamePaused ? 'paused' : 'active'}`}>
                  {settings.gamePaused ? 'Paused' : 'Active'}
                </span>
                <button
                  onClick={() => updateSetting('gamePaused', !settings.gamePaused)}
                  className={`admin-button ${settings.gamePaused ? 'resume-button' : 'pause-button'}`}
                  disabled={loading}
                >
                  {settings.gamePaused ? 'Resume Game' : 'Pause Game'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Node Max Stats Configuration */}
        <div className="admin-section">
          <h3 className="section-title">Node Max Stats Configuration</h3>
          <div className="node-max-stats-grid">
            {[1, 2, 3, 4, 5].map(tier => (
              <div key={tier} className="tier-max-stats">
                <h4 className="tier-title">Tier {tier}</h4>
                <div className="stat-inputs">
                  <div className="stat-input">
                    <label>Hash Power:</label>
                    <input
                      type="number"
                      value={settings.nodeMaxStats[tier]?.hashPower || ''}
                      onChange={(e) => {
                        const newStats = { ...settings.nodeMaxStats };
                        if (!newStats[tier]) newStats[tier] = { hashPower: '', bandwidth: '', uptime: '' };
                        newStats[tier].hashPower = e.target.value;
                        setSettings({ ...settings, nodeMaxStats: newStats });
                      }}
                      className="admin-input small"
                      placeholder="100"
                    />
                  </div>
                  <div className="stat-input">
                    <label>Bandwidth:</label>
                    <input
                      type="number"
                      value={settings.nodeMaxStats[tier]?.bandwidth || ''}
                      onChange={(e) => {
                        const newStats = { ...settings.nodeMaxStats };
                        if (!newStats[tier]) newStats[tier] = { hashPower: '', bandwidth: '', uptime: '' };
                        newStats[tier].bandwidth = e.target.value;
                        setSettings({ ...settings, nodeMaxStats: newStats });
                      }}
                      className="admin-input small"
                      placeholder="100"
                    />
                  </div>
                  <div className="stat-input">
                    <label>Uptime:</label>
                    <input
                      type="number"
                      value={settings.nodeMaxStats[tier]?.uptime || ''}
                      onChange={(e) => {
                        const newStats = { ...settings.nodeMaxStats };
                        if (!newStats[tier]) newStats[tier] = { hashPower: '', bandwidth: '', uptime: '' };
                        newStats[tier].uptime = e.target.value;
                        setSettings({ ...settings, nodeMaxStats: newStats });
                      }}
                      className="admin-input small"
                      placeholder="100"
                    />
                  </div>
                </div>
                <button 
                  onClick={() => updateNodeMaxStats(tier)}
                  className="admin-button small"
                  disabled={loading}
                >
                  Update Tier {tier}
                </button>
              </div>
            ))}
          </div>
          <div className="bulk-actions">
            <button 
              onClick={() => updateAllNodeMaxStats()}
              className="admin-button"
              disabled={loading}
            >
              Update All Node Max Stats
            </button>
          </div>
        </div>

        {/* Upgrade Boosts Configuration */}
        <div className="admin-section">
          <h3 className="section-title">Upgrade Boosts Configuration</h3>
          <div className="upgrade-boosts-grid">
            {[
              { rarity: 1, name: 'Common' },
              { rarity: 2, name: 'Uncommon' },
              { rarity: 3, name: 'Rare' },
              { rarity: 4, name: 'Epic' },
              { rarity: 5, name: 'Legendary' }
            ].map(({ rarity, name }) => (
              <div key={rarity} className="rarity-boost">
                <h4 className="rarity-title">{name}</h4>
                <div className="boost-input">
                  <label>Boost Amount:</label>
                  <input
                    type="number"
                    value={settings.upgradeBoosts[rarity - 1] || ''}
                    onChange={(e) => {
                      const newBoosts = [...settings.upgradeBoosts];
                      newBoosts[rarity - 1] = e.target.value;
                      setSettings({ ...settings, upgradeBoosts: newBoosts });
                    }}
                    className="admin-input small"
                    placeholder="10"
                  />
                  <button 
                    onClick={() => updateUpgradeBoost(rarity)}
                    className="admin-button small"
                    disabled={loading}
                  >
                    Update {name}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="bulk-actions">
            <button 
              onClick={() => updateAllUpgradeBoosts()}
              className="admin-button"
              disabled={loading}
            >
              Update All Upgrade Boosts
            </button>
          </div>
        </div>

        {/* Contract Balances */}
        <div className="admin-section">
          <h3 className="section-title">{t('admin.contract_balances')}</h3>
          <div className="balance-grid">
            <div className="balance-item">
              <span className="balance-label">GameLogic:</span>
              <span className="balance-value">{contractBalances.gameLogic} ETCPOWv2</span>
              <input
                type="number"
                placeholder="Amount (0 = all)"
                className="admin-input small"
              />
              <div className="balance-buttons">
                <button 
                  onClick={() => fundContract('gameLogic', '100')}
                  className="admin-button small"
                  disabled={loading}
                >
                  Fund 100
                </button>
                <button 
                  onClick={() => withdrawFromContract('gameLogic', '0')}
                  className="admin-button small withdraw"
                  disabled={loading}
                >
                  Withdraw
                </button>
              </div>
            </div>
            
            <div className="balance-item">
              <span className="balance-label">Achievements:</span>
              <span className="balance-value">{contractBalances.achievements} ETCPOWv2</span>
              <input
                type="number"
                placeholder="Amount"
                className="admin-input small"
              />
              <div className="balance-buttons">
                <button 
                  onClick={() => fundContract('achievements', '50')}
                  className="admin-button small"
                  disabled={loading}
                >
                  Fund 50
                </button>
                <button 
                  onClick={() => withdrawFromContract('achievements', '0')}
                  className="admin-button small withdraw"
                  disabled={loading}
                >
                  Withdraw
                </button>
              </div>
            </div>
            
            <div className="balance-item">
              <span className="balance-label">Rewards Pool:</span>
              <span className="balance-value">{contractBalances.rewardsPool} ETCPOWv2</span>
              <span className="balance-note">Pays all claims/referrals/achievements — keep funded</span>
              <div className="balance-buttons">
                <button
                  onClick={() => fundRewardsPool('100')}
                  className="admin-button small"
                  disabled={loading}
                >
                  Deposit 100
                </button>
                <button
                  onClick={() => fundRewardsPool('1000')}
                  className="admin-button small"
                  disabled={loading}
                >
                  Deposit 1000
                </button>
                <button 
                  onClick={() => withdrawFromContract('rewardsPool', '0')}
                  className="admin-button small withdraw"
                  disabled={loading}
                >
                  Emergency Withdraw
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ------------------------------------------------------------------
           Admin Panel — glass card language with amber "owner" accent.
           All selectors scoped under .admin-panel to avoid global bleed.
           ------------------------------------------------------------------ */
        .admin-panel {
          background: linear-gradient(170deg, rgba(6, 17, 13, 0.92), rgba(2, 8, 6, 0.97));
          border: 1px solid rgba(251, 191, 36, 0.3);
          border-radius: 16px;
          backdrop-filter: blur(18px);
          box-shadow:
            0 30px 60px rgba(0, 0, 0, 0.55),
            inset 0 1px 0 rgba(251, 191, 36, 0.08);
          position: relative;
          width: min(1200px, 96vw);
          max-width: 95vw;
          height: min(88vh, 900px);
          max-height: 92vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          color: #d8fff1;
          font-family: 'IBM Plex Mono', monospace;
        }

        .admin-panel .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          padding: 18px 22px;
          border-bottom: 1px solid rgba(251, 191, 36, 0.2);
          background: linear-gradient(90deg, rgba(251, 191, 36, 0.06), rgba(0, 255, 136, 0.03));
        }

        .admin-panel .panel-title {
          font-family: 'Orbitron', sans-serif;
          font-size: 18px;
          font-weight: 700;
          color: #fbbf24;
          text-transform: uppercase;
          letter-spacing: 3px;
          text-shadow: none;
        }

        .admin-panel .panel-stats {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-right: 0;
        }

        .admin-panel .stat-item {
          color: #fbbf24;
          font-weight: 600;
          font-size: 11px;
          padding: 5px 12px;
          background: rgba(251, 191, 36, 0.07);
          border: 1px solid rgba(251, 191, 36, 0.35);
          border-radius: 999px;
          text-transform: uppercase;
          letter-spacing: 0.12em;
        }

        .admin-panel .close-button {
          position: static;
          flex: 0 0 auto;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(251, 191, 36, 0.35);
          border-radius: 10px;
          background: rgba(251, 191, 36, 0.06);
          color: #fbbf24;
          font-size: 20px;
          line-height: 1;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .admin-panel .close-button:hover {
          border-color: rgba(251, 191, 36, 0.7);
          background: rgba(251, 191, 36, 0.14);
          box-shadow: 0 0 14px rgba(251, 191, 36, 0.25);
          transform: none;
        }

        .admin-panel .admin-content {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 20px 22px;
        }

        .admin-panel .admin-content::-webkit-scrollbar {
          width: 8px;
        }

        .admin-panel .admin-content::-webkit-scrollbar-thumb {
          background: rgba(251, 191, 36, 0.25);
          border-radius: 4px;
        }

        .admin-panel .admin-section {
          margin-bottom: 18px;
          padding: 18px;
          background: rgba(251, 191, 36, 0.02);
          border: 1px solid rgba(251, 191, 36, 0.16);
          border-radius: 14px;
        }

        .admin-panel .section-title {
          font-family: 'Orbitron', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: #fbbf24;
          margin: 0 0 16px 0;
          text-transform: uppercase;
          letter-spacing: 0.14em;
        }

        .admin-panel .control-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
          gap: 14px;
        }

        .admin-panel .control-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 14px;
          border: 1px solid rgba(0, 255, 136, 0.12);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.015);
        }

        .admin-panel .control-item label {
          font-size: 10px;
          color: rgba(125, 255, 194, 0.55);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
        }

        .admin-panel .admin-input {
          background: rgba(2, 8, 6, 0.8);
          border: 1px solid rgba(0, 255, 136, 0.22);
          border-radius: 10px;
          color: #e8fff5;
          font-family: 'IBM Plex Mono', monospace;
          padding: 9px 12px;
          font-size: 13px;
          width: 100%;
          box-sizing: border-box;
          transition: border-color 0.18s ease, box-shadow 0.18s ease;
        }

        .admin-panel .admin-input:focus {
          outline: none;
          border-color: rgba(0, 255, 136, 0.55);
          box-shadow: 0 0 12px rgba(0, 255, 136, 0.15);
        }

        .admin-panel .admin-input.small {
          padding: 6px 10px;
          font-size: 12px;
        }

        .admin-panel .admin-button {
          background: rgba(251, 191, 36, 0.07);
          border: 1px solid rgba(251, 191, 36, 0.35);
          border-radius: 10px;
          color: #fbbf24;
          font-family: 'IBM Plex Mono', monospace;
          padding: 8px 16px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          transition: all 0.18s ease;
        }

        .admin-panel .admin-button:hover:not(:disabled) {
          border-color: rgba(251, 191, 36, 0.7);
          box-shadow: 0 0 14px rgba(251, 191, 36, 0.2);
          transform: translateY(-1px);
        }

        .admin-panel .admin-button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .admin-panel .admin-button.small {
          padding: 5px 10px;
          font-size: 10px;
        }

        .admin-panel .admin-button.secondary {
          background: rgba(216, 255, 241, 0.03);
          border-color: rgba(216, 255, 241, 0.2);
          color: rgba(216, 255, 241, 0.6);
        }

        .admin-panel .admin-button.secondary:hover:not(:disabled) {
          border-color: rgba(216, 255, 241, 0.4);
          box-shadow: none;
        }

        .admin-panel .chest-tier-prices {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
          gap: 8px;
          margin: 8px 0;
        }

        .admin-panel .tier-price-input {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 9px;
          background: rgba(0, 255, 136, 0.03);
          border: 1px solid rgba(0, 255, 136, 0.18);
          border-radius: 10px;
        }

        .admin-panel .tier-price-input span {
          font-size: 10px;
          color: #7dffc2;
          min-width: 56px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .admin-panel .tier-price-buttons {
          display: flex;
          gap: 10px;
          margin-top: 10px;
        }

        .admin-panel .achievement-rewards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 8px;
          margin: 8px 0;
          max-height: 260px;
          overflow-y: auto;
        }

        .admin-panel .achievement-reward-input {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 6px;
          padding: 10px;
          background: rgba(20, 184, 166, 0.04);
          border: 1px solid rgba(20, 184, 166, 0.25);
          border-radius: 10px;
        }

        .admin-panel .achievement-reward-input span {
          font-size: 10px;
          color: #5eead4;
          min-width: 0;
          line-height: 1.35;
          white-space: normal;
          word-break: break-word;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .admin-panel .achievement-reward-buttons {
          display: flex;
          gap: 10px;
          margin-top: 10px;
        }

        .admin-panel .node-max-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 12px;
          margin: 12px 0;
        }

        .admin-panel .tier-max-stats {
          background: rgba(0, 255, 136, 0.03);
          border: 1px solid rgba(0, 255, 136, 0.18);
          border-radius: 12px;
          padding: 16px;
        }

        .admin-panel .tier-title {
          font-family: 'Orbitron', sans-serif;
          color: #7dffc2;
          font-size: 13px;
          font-weight: 600;
          margin: 0 0 12px 0;
          text-align: center;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .admin-panel .stat-inputs {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;
        }

        .admin-panel .stat-input {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .admin-panel .stat-input label {
          min-width: 84px;
          font-size: 10px;
          color: rgba(216, 255, 241, 0.55);
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .admin-panel .upgrade-boosts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin: 12px 0;
        }

        .admin-panel .rarity-boost {
          background: rgba(251, 191, 36, 0.03);
          border: 1px solid rgba(251, 191, 36, 0.25);
          border-radius: 12px;
          padding: 14px;
          text-align: center;
        }

        .admin-panel .rarity-title {
          font-family: 'Orbitron', sans-serif;
          color: #fbbf24;
          font-size: 12px;
          font-weight: 600;
          margin: 0 0 10px 0;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .admin-panel .boost-input {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
        }

        .admin-panel .boost-input label {
          font-size: 10px;
          color: rgba(216, 255, 241, 0.55);
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .admin-panel .bulk-actions {
          display: flex;
          justify-content: center;
          margin-top: 14px;
          padding-top: 14px;
          border-top: 1px solid rgba(251, 191, 36, 0.14);
        }

        .admin-panel .pause-button {
          background: rgba(251, 191, 36, 0.08);
          border-color: rgba(251, 191, 36, 0.5);
          color: #fbbf24;
        }

        .admin-panel .resume-button {
          background: rgba(0, 255, 136, 0.08);
          border-color: rgba(0, 255, 136, 0.45);
          color: #00ff88;
        }

        .admin-panel .resume-button:hover:not(:disabled) {
          border-color: rgba(0, 255, 136, 0.7);
          box-shadow: 0 0 14px rgba(0, 255, 136, 0.2);
        }

        .admin-panel .game-status {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .admin-panel .status-indicator {
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .admin-panel .status-indicator.active {
          background: rgba(0, 255, 136, 0.1);
          color: #00ff88;
          border: 1px solid rgba(0, 255, 136, 0.45);
        }

        .admin-panel .status-indicator.paused {
          background: rgba(251, 191, 36, 0.08);
          color: #fbbf24;
          border: 1px solid rgba(251, 191, 36, 0.45);
        }

        .admin-panel .balance-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 12px;
        }

        .admin-panel .balance-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 14px;
          background: rgba(255, 255, 255, 0.015);
          border: 1px solid rgba(251, 191, 36, 0.16);
          border-radius: 12px;
        }

        .admin-panel .balance-label {
          font-size: 10px;
          color: rgba(125, 255, 194, 0.55);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
        }

        .admin-panel .balance-value {
          font-family: 'Orbitron', sans-serif;
          font-size: 15px;
          font-weight: 700;
          color: #fbbf24;
        }

        .admin-panel .balance-note {
          font-size: 10px;
          color: rgba(216, 255, 241, 0.4);
          font-style: italic;
        }

        .admin-panel .balance-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .admin-panel .admin-button.withdraw {
          background: rgba(216, 255, 241, 0.03);
          border-color: rgba(216, 255, 241, 0.25);
          color: rgba(216, 255, 241, 0.7);
        }

        .admin-panel .admin-button.withdraw:hover:not(:disabled) {
          border-color: rgba(251, 191, 36, 0.6);
          color: #fbbf24;
        }

        @media (max-width: 900px) {
          .admin-panel .control-grid {
            grid-template-columns: 1fr;
          }

          .admin-panel .node-max-stats-grid,
          .admin-panel .upgrade-boosts-grid,
          .admin-panel .balance-grid {
            grid-template-columns: 1fr;
          }

          .admin-panel .admin-content {
            padding: 12px;
          }
        }
      `}</style>
    </div>
  );
};
